import React, { Component } from 'react';
import PropTypes from 'prop-types';
import '../leadsCardNew/index.scss';
import './index.scss';

const SUPPORTED_PRODUCTS = ['sem_wap', 'sem_pc'];
const FORBIDDEN_EXTRA_PARAM_KEYS = ['sign', 'ts', 'nonce'];
const signServiceToken = 'bc49ac1e2dfa136691e38d03e12f4bfdec83ad02dbb73e59a92ead23233ad804';

const isPre = window.location.href.includes("pre-p4psearch.1688.com") || window.location.href.includes("preMode");

const getProductPath = product => product.replace('sem_', '');

const getGatewayOrigin = () => {
  const gatewayDomain = isPre
    ? 'pre-tagore-growth-gateway.1688.com'
    : 'tagore-growth-gateway.1688.com';

  return `https://${gatewayDomain}`;
};

const joinUrl = (origin, path) => {
  if (!origin) {
    return path;
  }

  return `${origin.replace(/\/$/, '')}${path}`;
};

const getResponseBody = async (response) => {
  if (!response || typeof response.json !== 'function') {
    return response;
  }

  const body = await response.json();

  if (!response.ok) {
    throw new Error((body && body.message) || '签名入口请求失败');
  }

  return body;
};

const createAbortError = () => {
  const error = new Error('签名请求已取消');
  error.name = 'AbortError';
  return error;
};

const isAbortError = error => error && error.name === 'AbortError';

const waitForRequest = (request, signal) => new Promise((resolve, reject) => {
  let settled = false;
  let abortRequest;
  const removeAbortListener = () => {
    if (signal && typeof signal.removeEventListener === 'function') {
      signal.removeEventListener('abort', abortRequest);
    }
  };
  const complete = callback => (value) => {
    if (settled) {
      return;
    }

    settled = true;
    removeAbortListener();
    callback(value);
  };

  abortRequest = () => complete(reject)(createAbortError());
  if (signal && typeof signal.addEventListener === 'function') {
    signal.addEventListener('abort', abortRequest, { once: true });
  }
  if (signal && signal.aborted) {
    abortRequest();
    return;
  }
  Promise.resolve(request).then(complete(resolve), complete(reject));
});

const ensureGatewayPreconnect = (origin) => {
  if (typeof document === 'undefined' || !document.head) {
    return;
  }

  ['preconnect', 'dns-prefetch'].forEach((rel) => {
    const selector = `link[data-agent-a2a-link="${rel}"]`;
    if (document.head.querySelector(selector)) {
      return;
    }

    const link = document.createElement('link');
    link.rel = rel;
    link.href = origin;
    link.dataset.agentA2aLink = rel;
    if (rel === 'preconnect') {
      link.crossOrigin = 'anonymous';
    }
    document.head.appendChild(link);
  });
};

const validateExtraParams = (extraParams) => {
  const forbiddenKey = Object.keys(extraParams).find(key => FORBIDDEN_EXTRA_PARAM_KEYS.includes(key));

  if (forbiddenKey) {
    throw new Error(`extraParams 不能包含 ${forbiddenKey}`);
  }
};

const getSignExtraParams = (extraParams, leadsId) => {
  if (!leadsId) {
    return extraParams || {};
  }

  return Object.assign({}, extraParams || {}, { leadsId });
};

const createSignRequestPayload = (props) => {
  const {
    appKey,
    cna,
    extraParams,
    leadsId,
    product,
    pseudoDeviceId,
    query,
    source,
    uid,
  } = props;

  if (!SUPPORTED_PRODUCTS.includes(product)) {
    throw new Error('product 只支持 sem_wap 或 sem_pc');
  }

  const signExtraParams = getSignExtraParams(extraParams, leadsId);
  validateExtraParams(signExtraParams);

  const payload = {
    appKey: appKey || product,
    query,
    pseudoDeviceId,
    cna,
    uid,
    source: source || product,
    extraParams: Object.keys(signExtraParams).length > 0 ? signExtraParams : undefined,
  };

  return Object.keys(payload).reduce((params, key) => (
    payload[key] !== undefined && payload[key] !== null && payload[key] !== ''
      ? Object.assign({}, params, { [key]: payload[key] })
      : params
  ), {});
};

const requestSignUrl = async (product, requestPayload, signal) => {
  const signUrlPath = `/a2a/sem/${getProductPath(product)}/auth/sign-url`;
  const gatewayOrigin = getGatewayOrigin();
  const signRequest = getResponseBody(window.fetch(joinUrl(gatewayOrigin, signUrlPath), {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${signServiceToken}`,
    },
    body: JSON.stringify(requestPayload),
  }));
  const result = await waitForRequest(signRequest, signal).then((response) => response.json());

  if (!result || !result.success || !result.url) {
    throw new Error((result && result.message) || '未获取到已签名会话地址');
  }

  const newUrl = `${result.url}&sign=${result.sign || ''}`

  return {
    result,
    sessionUrl: /^https?:\/\//.test(newUrl)
      ? newUrl
      : joinUrl(gatewayOrigin, newUrl),
  };
};

const hasRequestParamsChanged = (previousProps, props) => [
  'appKey',
  'cna',
  'extraParams',
  'leadsId',
  'product',
  'pseudoDeviceId',
  'query',
  'source',
  'uid',
].some(key => previousProps[key] !== props[key]);

/**
 * 请求 SEM 签名入口并在 iframe 中加载会话页。
 * 密钥只应保存在服务端。
 */
class AgentA2a extends Component {
  constructor(props) {
    super(props);

    this.state = {
      sessionUrl: '',
      status: 'signing',
      errorMessage: '',
      hasSessionReady: false,
      visible: true,
    };
    this.closed = false;
    this.unmounted = false;
    this.requestAbortController = null;
    this.requestId = 0;
    this.handleClose = this.handleClose.bind(this);
    this.handleFrameError = this.handleFrameError.bind(this);
    this.handleFrameLoad = this.handleFrameLoad.bind(this);
  }

  componentDidMount() {
    ensureGatewayPreconnect(getGatewayOrigin());
    this.startSessionRequest();
  }

  componentDidUpdate(previousProps) {
    if (hasRequestParamsChanged(previousProps, this.props)) {
      this.startSessionRequest();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.cancelRequest();
  }

  isRequestActive(requestId) {
    return !this.unmounted && !this.closed && requestId === this.requestId;
  }

  cancelRequest() {
    if (this.requestAbortController) {
      this.requestAbortController.abort();
    }
    this.requestAbortController = null;
  }

  startSessionRequest() {
    this.cancelRequest();
    this.requestId += 1;
    this.requestSession(this.requestId);
  }

  notifyError(error) {
    if (typeof this.props.onError === 'function') {
      this.props.onError(error);
    }
  }

  async requestSession(requestId) {
    const {
      appKey,
      cna,
      extraParams,
      leadsId,
      product,
      pseudoDeviceId,
      query,
      source,
      uid,
    } = this.props;
    const requestProps = {
      appKey,
      cna,
      extraParams,
      leadsId,
      product,
      pseudoDeviceId,
      query,
      source,
      uid,
    };
    const controller = typeof AbortController === 'undefined' ? null : new AbortController();
    const signal = controller ? controller.signal : undefined;

    this.requestAbortController = controller;
    this.setState({
      status: 'signing',
      errorMessage: '',
      sessionUrl: '',
    });

    try {
      const requestPayload = createSignRequestPayload(requestProps);
      const response = await requestSignUrl(requestProps.product, requestPayload, signal);

      if (!this.isRequestActive(requestId)) {
        return;
      }

      this.setState({
        sessionUrl: response.sessionUrl,
        status: 'frameLoading',
        hasSessionReady: true,
      }, () => {
        if (typeof this.props.onSessionReady === 'function') {
          this.props.onSessionReady(response.result, response.sessionUrl);
        }
        if (typeof this.props.onSuccess === 'function') {
          this.props.onSuccess(response.result, response.sessionUrl);
        }
      });
    } catch (error) {
      if (!this.isRequestActive(requestId) || isAbortError(error)) {
        return;
      }

      this.setState({
        status: 'error',
        errorMessage: (error && error.message) || '连接采购助手失败，请重试',
      });
      this.notifyError(error);
    } finally {
      if (this.requestAbortController === controller) {
        this.requestAbortController = null;
      }
    }
  }

  handleFrameLoad() {
    if (!this.closed) {
      this.setState({ status: 'ready' });
    }
  }

  handleFrameError() {
    if (this.closed) {
      return;
    }

    const error = new Error('采购助手页面加载失败，请重试');

    this.setState({
      sessionUrl: '',
      status: 'error',
      errorMessage: error.message,
    });
    this.notifyError(error);
  }

  handleClose() {
    this.closed = true;
    this.cancelRequest();
    this.setState({
      status: 'closed',
      visible: false,
    });
    if (typeof this.props.onClose === 'function') {
      this.props.onClose();
    }
  }

  render() {
    const {
      className,
      hideUntilSessionReady,
      iframeTitle,
      style,
    } = this.props;
    const {
      errorMessage,
      hasSessionReady,
      sessionUrl,
      status,
      visible,
    } = this.state;

    if (!visible || (hideUntilSessionReady && !hasSessionReady && status === 'signing')) {
      return null;
    }

    return (
      <div
        className={`leads-card-container-new agent-a2a-container${className ? ` ${className}` : ''}`}
        style={style}
      >
        <div className="leads-card-content">
          <button
            className="agent-a2a-close-button"
            type="button"
            aria-label="关闭 Agent 浮层"
            onClick={this.handleClose}
          />
          {sessionUrl && (
            <iframe
              className="agent-a2a-session-frame"
              src={sessionUrl}
              title={iframeTitle}
              onError={this.handleFrameError}
              onLoad={this.handleFrameLoad}
            />
          )}
          {status === 'frameLoading' && (
            <div className="agent-a2a-frame-loading-mask" data-testid="agent-frame-loading" role="status">
              <span className="agent-a2a-loading-indicator" />
              正在加载采购助手...
            </div>
          )}
          {status === 'error' && (
            <div className="agent-a2a-error-panel" data-testid="agent-error" role="alert">
              <span>{errorMessage}</span>
            </div>
          )}
        </div>
      </div>
    );
  }
}

AgentA2a.defaultProps = {
  appKey: '',
  className: '',
  cna: '',
  extraParams: {},
  hideUntilSessionReady: false,
  iframeTitle: '采购助手会话',
  leadsId: '',
  onClose: undefined,
  onError: undefined,
  onSessionReady: undefined,
  onSuccess: undefined,
  product: 'sem_wap',
  pseudoDeviceId: '',
  query: '',
  source: '',
  style: undefined,
  uid: '',
};

AgentA2a.propTypes = {
  appKey: PropTypes.string,
  className: PropTypes.string,
  cna: PropTypes.string,
  extraParams: PropTypes.shape({}),
  hideUntilSessionReady: PropTypes.bool,
  iframeTitle: PropTypes.string,
  leadsId: PropTypes.string,
  onClose: PropTypes.func,
  onError: PropTypes.func,
  onSessionReady: PropTypes.func,
  onSuccess: PropTypes.func,
  product: PropTypes.oneOf(SUPPORTED_PRODUCTS),
  pseudoDeviceId: PropTypes.string,
  query: PropTypes.string,
  source: PropTypes.string,
  style: PropTypes.shape({}),
  uid: PropTypes.string,
};

export default AgentA2a;
